import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const PayrollPeriodSelector = ({ selectedPeriod, onPeriodChange, processingStatus, onProcessingModeChange }) => {
  const [processingMode, setProcessingMode] = useState('manual');

  // Generate period options dynamically (last 24 months + next 3 months)
  const generatePeriodOptions = () => {
    const options = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11
    
    // Start from 24 months ago
    const startDate = new Date(currentYear, currentMonth - 23, 1);
    // End at 3 months in the future
    const endDate = new Date(currentYear, currentMonth + 3, 1);
    
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    let date = new Date(startDate);
    while (date <= endDate) {
      const year = date.getFullYear();
      const month = date.getMonth();
      const value = `${year}-${String(month + 1).padStart(2, '0')}`;
      const label = `${monthNames[month]} ${year}`;
      
      options.push({ value, label });
      
      // Move to next month
      date.setMonth(date.getMonth() + 1);
    }
    
    // Reverse so most recent months appear first
    return options.reverse();
  };

  const periodOptions = generatePeriodOptions();

  const handleModeChange = (mode) => {
    setProcessingMode(mode);
    onProcessingModeChange(mode);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success bg-success/10 border-success/20';
      case 'processing':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'pending':
        return 'text-muted-foreground bg-muted border-border';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Period Selection */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Payroll Period</h2>
              <p className="text-sm text-muted-foreground">Select processing period</p>
            </div>
          </div>
          
          <Select
            options={periodOptions}
            value={selectedPeriod}
            onChange={onPeriodChange}
            placeholder="Select period"
            className="w-48"
          />
        </div>

        {/* Processing Status & Mode */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-foreground">Status:</span>
            <div className={`px-3 py-1 rounded-full border text-sm font-medium ${getStatusColor(processingStatus)}`}>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-current" />
                {processingStatus === 'completed' && 'Completed'}
                {processingStatus === 'processing' && 'Processing'}
                {processingStatus === 'pending' && 'Pending'}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
            <Button
              variant={processingMode === 'manual' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleModeChange('manual')}
              className="text-xs"
            >
              <Icon name="Hand" size={14} className="mr-1" />
              Manual
            </Button>
            <Button
              variant={processingMode === 'automatic' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleModeChange('automatic')}
              className="text-xs"
            >
              <Icon name="Zap" size={14} className="mr-1" />
              Automatic
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollPeriodSelector;